import React, { useState, useEffect } from 'react'
import { Sparkles, Video, Camera, ShoppingBag, Star, MessageSquare, Smartphone, Instagram, Music, Youtube, ChevronRight, ChevronLeft, Zap, Target, Users, TrendingUp, Package, Palette, X, Check, ArrowRight, Lightbulb } from 'lucide-react'

const ProjectWizard = ({ onComplete, onSkip }) => {
  const [step, setStep] = useState(0)

  // Body-scroll-lock zolang de wizard open is: voorkomt dat de pagina eronder
  // (met de vaste mobiele CTA-balk) meescrolt bij vegen in de optielijst.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = previous }
  }, [])
  const [answers, setAnswers] = useState({
    goal: null,
    contentType: null,
    platform: null,
    quantity: null,
    hasProduct: null
  })

  const steps = [
    {
      question: 'Wat is je doel met UGC?',
      key: 'goal',
      options: [
        { value: 'sales', icon: ShoppingBag, label: 'Meer verkopen', desc: 'Conversie-gerichte content voor ads of productpagina\'s', color: 'from-emerald-400 to-emerald-600' },
        { value: 'awareness', icon: TrendingUp, label: 'Merkbekendheid', desc: 'Bereik nieuwe doelgroepen met authentieke content', color: 'from-blue-400 to-blue-600' },
        { value: 'social-proof', icon: Star, label: 'Social proof', desc: 'Reviews en testimonials voor vertrouwen', color: 'from-amber-400 to-amber-600' },
        { value: 'social-content', icon: Instagram, label: 'Social media content', desc: 'Feed vullen met authentieke posts en reels', color: 'from-pink-400 to-pink-600' }
      ]
    },
    {
      question: 'Welk type content heb je nodig?',
      key: 'contentType',
      options: [
        { value: 'product-review', icon: Star, label: 'Product Review', desc: 'Creator bespreekt en toont je product', color: 'from-amber-400 to-orange-500' },
        { value: 'unboxing', icon: Package, label: 'Unboxing', desc: 'Eerste reactie bij het openen van je product', color: 'from-purple-400 to-purple-600' },
        { value: 'tutorial', icon: Lightbulb, label: 'Tutorial / How-to', desc: 'Creator laat zien hoe je product werkt', color: 'from-cyan-400 to-cyan-600' },
        { value: 'testimonial', icon: MessageSquare, label: 'Testimonial', desc: 'Persoonlijke ervaring en aanbeveling', color: 'from-emerald-400 to-emerald-600' },
        { value: 'lifestyle', icon: Camera, label: 'Lifestyle / In-use', desc: 'Product geïntegreerd in het dagelijks leven', color: 'from-rose-400 to-rose-600' },
        { value: 'ad-creative', icon: Zap, label: 'Ad Creative', desc: 'Korte, pakkende video voor advertenties', color: 'from-blue-400 to-indigo-600' }
      ]
    },
    {
      question: 'Voor welk platform is de content?',
      key: 'platform',
      options: [
        { value: 'tiktok', icon: Music, label: 'TikTok', desc: 'Verticale video\'s, 15-60 sec', color: 'from-gray-800 to-gray-900' },
        { value: 'instagram', icon: Instagram, label: 'Instagram Reels', desc: 'Verticale video\'s, 15-90 sec', color: 'from-pink-500 to-purple-600' },
        { value: 'meta', icon: Youtube, label: 'Meta advertenties', desc: 'Voor Facebook- en Instagram-ads', color: 'from-blue-500 to-blue-700' },
        { value: 'multi', icon: Smartphone, label: 'Meerdere platforms', desc: 'Content geschikt voor alle kanalen', color: 'from-blue-500 to-purple-500' }
      ]
    },
    {
      question: 'Hoeveel video\'s heb je nodig?',
      key: 'quantity',
      options: [
        { value: '1', icon: Video, label: '1 video', desc: 'Perfect om te testen', color: 'from-gray-400 to-gray-500' },
        { value: '2-3', icon: Video, label: '2-3 video\'s', desc: 'Meest gekozen — genoeg om te A/B testen', color: 'from-blue-400 to-blue-600' },
        { value: '4-6', icon: Video, label: '4-6 video\'s', desc: 'Ideaal voor een volledige campagne', color: 'from-purple-400 to-purple-600' },
        { value: '7+', icon: Video, label: '7+ video\'s', desc: 'Voor grote campagnes of meerdere creators', color: 'from-emerald-400 to-emerald-600' }
      ]
    },
    {
      question: 'Stuur je een fysiek product op?',
      key: 'hasProduct',
      options: [
        { value: 'yes', icon: Package, label: 'Ja, ik stuur een product', desc: 'Creator ontvangt het product thuis', color: 'from-emerald-400 to-emerald-600' },
        { value: 'no', icon: Palette, label: 'Nee, het is een dienst of digitaal', desc: 'Geen fysiek product nodig', color: 'from-slate-400 to-slate-600' }
      ]
    }
  ]

  const handleSelect = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }))
    if (step < steps.length - 1) {
      setTimeout(() => setStep(step + 1), 200)
    }
  }

  const buildFormData = () => {
    const contentTypeLabels = {
      'product-review': 'Product Review',
      'unboxing': 'Unboxing Video',
      'tutorial': 'Tutorial / How-to',
      'testimonial': 'Testimonial',
      'lifestyle': 'Lifestyle Content',
      'ad-creative': 'Ad Creative'
    }

    const platformLabels = {
      'tiktok': 'TikTok',
      'instagram': 'Instagram Reels',
      'meta': 'Meta advertenties',
      'multi': 'TikTok, Instagram Reels & Meta'
    }

    // Vertaling naar de kanaal-ids van het brief-formulier. Zonder deze mapping
    // liet de wizard het verplichte kanaal-veld leeg achter en was de brief na
    // vijf vragen alsnog niet plaatsbaar.
    const platformChannels = {
      'tiktok': ['tiktok'],
      'instagram': ['instagram'],
      'meta': ['meta'],
      'multi': ['tiktok', 'instagram', 'meta']
    }

    const goalDescriptions = {
      'sales': 'Doel: conversie en verkopen stimuleren. Focus op sterke call-to-actions en productvoordelen.',
      'awareness': 'Doel: merkbekendheid vergroten. Focus op herkenbaar merkgevoel en breed bereik.',
      'social-proof': 'Doel: vertrouwen opbouwen met social proof. Focus op eerlijke, authentieke ervaringen.',
      'social-content': 'Doel: social media kanalen vullen met authentieke content. Focus op visueel aantrekkelijke, deelbare content.'
    }

    // Prijs per video op basis van content type complexiteit
    const pricePerVideo = {
      'product-review': 75,
      'unboxing': 75,
      'testimonial': 50,
      'lifestyle': 100,
      'tutorial': 125,
      'ad-creative': 150
    }

    // Gemiddeld aantal video's per keuze
    const quantityMultiplier = {
      '1': 1,
      '2-3': 3,
      '4-6': 5,
      '7+': 8
    }

    const basePrice = pricePerVideo[answers.contentType] || 75
    const videoCount = quantityMultiplier[answers.quantity] || 1
    const calculatedBudget = basePrice * videoCount

    const contentType = contentTypeLabels[answers.contentType] || ''
    const platform = platformLabels[answers.platform] || ''
    const quantity = answers.quantity || '1'

    // Build requirements
    const requirements = []
    requirements.push(`${quantity === '1' ? '1' : quantity} ${contentType.toLowerCase()} video('s)`)
    requirements.push(`Formaat: verticaal (9:16) voor ${platform}`)
    if (answers.contentType === 'ad-creative') {
      requirements.push('Haak/hook in de eerste 3 seconden')
      requirements.push('Sterke call-to-action aan het einde')
    }
    if (answers.contentType === 'unboxing') {
      requirements.push('Eerste reactie bij openen moet authentiek zijn')
      requirements.push('Laat het product duidelijk in beeld komen')
    }
    if (answers.contentType === 'testimonial') {
      requirements.push('Persoonlijke ervaring delen')
      requirements.push('Eerlijk en authentiek, geen script-gevoel')
    }
    if (answers.contentType === 'tutorial') {
      requirements.push('Stap-voor-stap uitleg')
      requirements.push('Duidelijk laten zien hoe het product werkt')
    }

    // Build deliverables
    const deliverables = []
    deliverables.push({ title: `${quantity === '1' ? '1' : quantity} afgewerkte video('s) in hoge kwaliteit`, description: '' })
    deliverables.push({ title: 'Ruwe bestanden (raw footage)', description: '' })
    if (answers.platform === 'multi') {
      deliverables.push({ title: 'Versies aangepast per platform', description: '' })
    }

    // Build description
    const description = `Wij zoeken een creator voor het maken van ${contentType.toLowerCase()} content voor ${platform}.

${goalDescriptions[answers.goal] || ''}

Wat we verwachten:
- ${quantity === '1' ? '1' : quantity} professionele video('s)
- Verticaal formaat (9:16)
- Authentieke, natuurlijke stijl
${answers.hasProduct === 'yes' ? '- Product wordt naar je opgestuurd\n' : ''}`

    // Voorstel voor de deadline: drie weken vooruit. Bewust een werkbare standaard
    // en geen leeg veld — de gebruiker kan hem daarna gewoon aanpassen.
    const suggestedDeadline = new Date()
    suggestedDeadline.setDate(suggestedDeadline.getDate() + 21)

    return {
      title: '',
      description,
      contentRequirements: requirements,
      deliverables,
      budget: calculatedBudget,
      channels: platformChannels[answers.platform] || [],
      deadline: suggestedDeadline.toISOString().slice(0, 10),
      willSendProduct: answers.hasProduct === 'yes',
      category: '',
      preferredCreatorType: ''
    }
  }

  const allAnswered = Object.values(answers).every(v => v !== null)
  const currentStepData = steps[step]
  const progress = ((step + 1) / steps.length) * 100

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4">
      <div className="relative w-full max-w-2xl bg-white rounded-none sm:rounded-3xl shadow-2xl overflow-hidden h-full sm:h-auto max-h-[100dvh] sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-gradient-to-r from-[#1D8BF8] to-[#7c3aed] rounded-xl flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-[#0F172A]">Opdracht Assistent</h2>
                <p className="text-xs text-[#475569]">We helpen je de perfecte opdracht samen te stellen</p>
              </div>
            </div>
            <button onClick={onSkip} className="p-2.5 -mr-1 text-[#475569] hover:text-[#0F172A] hover:bg-gray-100 rounded-lg transition flex-shrink-0" title="Zelf invullen" aria-label="Sluiten en zelf invullen">
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Progress */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#1D8BF8] to-[#7c3aed] transition-all duration-500 ease-out" style={{ width: `${progress}%` }} />
            </div>
            <span className="text-xs text-[#475569] font-medium">{step + 1}/{steps.length}</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6">
          {!allAnswered || step < steps.length - 1 ? (
            <>
              <h3 className="text-xl font-bold text-[#0F172A] mb-1">{currentStepData.question}</h3>
              <p className="text-sm text-[#475569] mb-6">Kies de optie die het beste past bij jouw situatie</p>

              <div className={`grid gap-3 ${currentStepData.options.length <= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2'}`}>
                {currentStepData.options.map(option => {
                  const Icon = option.icon
                  const isSelected = answers[currentStepData.key] === option.value
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleSelect(currentStepData.key, option.value)}
                      className={`relative text-left p-4 rounded-2xl border-2 transition-all duration-200 group hover:shadow-md ${
                        isSelected
                          ? 'border-[#1D8BF8] bg-blue-50/50 shadow-md'
                          : 'border-gray-100 hover:border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${option.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[#0F172A] text-sm">{option.label}</span>
                            {option.budget && (
                              <span className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full font-medium">~€{option.budget}</span>
                            )}
                          </div>
                          <p className="text-xs text-[#475569] mt-0.5">{option.desc}</p>
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 bg-[#3EB489] rounded-full flex items-center justify-center flex-shrink-0">
                            <Check className="w-3.5 h-3.5 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </>
          ) : (
            // Summary
            <div>
              <h3 className="text-xl font-bold text-[#0F172A] mb-1">Jouw opdracht samenvatting</h3>
              <p className="text-sm text-[#475569] mb-6">Op basis van je antwoorden stellen we het volgende voor</p>

              <div className="space-y-3">
                {steps.map((s, i) => {
                  const selected = s.options.find(o => o.value === answers[s.key])
                  if (!selected) return null
                  const Icon = selected.icon
                  return (
                    <div key={i} className="flex items-center gap-3 p-3 bg-[#F8FAFC] rounded-xl">
                      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${selected.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[#475569]">{s.question}</p>
                        <p className="text-sm font-semibold text-[#0F172A]">{selected.label}</p>
                      </div>
                      <button onClick={() => setStep(i)} className="text-xs text-[#1D8BF8] hover:underline flex-shrink-0">Wijzig</button>
                    </div>
                  )
                })}
              </div>

              {/* Budget indicatie */}
              {(() => {
                const priceMap = { 'product-review': 75, 'unboxing': 75, 'testimonial': 50, 'lifestyle': 100, 'tutorial': 125, 'ad-creative': 150 }
                const countMap = { '1': 1, '2-3': 3, '4-6': 5, '7+': 8 }
                const estimated = (priceMap[answers.contentType] || 75) * (countMap[answers.quantity] || 1)
                return (
                  <div className="mt-4 p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <p className="text-xs text-emerald-600 font-medium">Geschat budget</p>
                        <p className="text-2xl font-black text-emerald-700">€{estimated}<span className="text-sm font-medium text-emerald-500 ml-1">excl. BTW</span></p>
                      </div>
                      <div className="sm:text-right text-xs text-emerald-600">
                        <p>~€{(priceMap[answers.contentType] || 75)} per video</p>
                        <p>Je kunt dit nog aanpassen</p>
                      </div>
                    </div>
                  </div>
                )
              })()}

              <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                <div className="flex items-start gap-3">
                  <Lightbulb className="w-5 h-5 text-[#1D8BF8] flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-[#475569]">
                    <p className="font-medium text-[#0F172A] mb-1">We vullen het formulier alvast voor je in</p>
                    <p>Je kunt alles nog aanpassen. De titel en categorie vul je zelf in — die zijn uniek per opdracht.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {/* Vaste footer. Mobiel: primaire knop volle breedte bovenaan, daaronder Terug / Zelf invullen.
            Desktop (sm+): Terug links, Zelf invullen + primaire knop rechts (ongewijzigd). */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:pb-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
          <div className="order-2 sm:order-1">
            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 -ml-4 sm:ml-0 min-h-[44px] text-sm font-medium text-[#475569] hover:text-[#0F172A] transition inline-flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Terug
              </button>
            )}
          </div>

          <button onClick={onSkip} className="order-3 sm:order-2 sm:ml-auto px-4 py-2 -mr-4 sm:mr-0 min-h-[44px] text-sm text-[#475569] hover:text-[#0F172A] transition">
            Zelf invullen
          </button>
          {allAnswered && step === steps.length - 1 && (
            <button
              onClick={() => onComplete(buildFormData())}
              className="order-1 sm:order-3 w-full sm:w-auto px-6 py-3 sm:py-2.5 bg-gradient-to-r from-[#1D8BF8] to-[#7c3aed] text-white text-sm font-semibold rounded-xl hover:shadow-lg transition inline-flex items-center justify-center gap-2"
            >
              Formulier invullen
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProjectWizard
